import React, { useState, useEffect, useRef, useCallback } from 'react';

import { 
  Character,
  CharacterFeature,
  SFFeature,
  CharacterSpecialFeature,
} from "@dndModels";
import { DisablableOption } from '@models';

import DandDAPI from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import DataUtilities from '@dndUtilities/data_utilities';
import SelectStringBox from '@components/input/selects/SelectStringBox';
import LoadingBox from '@components/input/LoadingBox';

interface Props {
  char: Character;
  obj: CharacterFeature;
  shared: boolean;
}

const api = DandDAPI.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const data_util = DataUtilities.getInstance();

export default function CharacterSpecialFeatureEffect({
  char,
  obj,
  shared,
}: Props) {
  const fos = obj.feature_options as CharacterSpecialFeature[];
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  // const [special_features, setSpecialFeatures] = useState<SpecialFeature[]>([]);
  const [options, setOptions] = useState<DisablableOption[]>([]);
  const [selected, setSelected] = useState(fos.filter(o => o.source_id !== "").map(o => o.source_id)[0]);
  const [processing, setProcessing] = useState(false);

  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const recalculated = useCallback(() => {
    setReloading(true);
  }, [setReloading]);

  const load = useCallback(() => {
    if (obj.floating_feature instanceof SFFeature) {
      const sff = obj.floating_feature;
      const ids = sff.special_feature_ids;
      const type = sff.special_feature_type;
      let new_sfs = [...data_util.special_features];
      if (ids.length > 0) {
        new_sfs = new_sfs.filter(o => ids.includes(o._id));
      } else if (type !== "") {
        new_sfs = new_sfs.filter(o => o.special_feature_type === type);
      }
      new_sfs = new_sfs.filter(o => 
        char_util.fitLimits(char, o.limitations, "special_feature", o._id) === "true");
      // setSpecialFeatures(new_sfs);
      let new_options: DisablableOption[] = new_sfs.map(o => {
        const opt: DisablableOption = {
          id: o._id, text: o.name
        };
        return opt;
      });
      if (sff.allow_none) {
        new_options = [{ id: "", text: "None" },...new_options];
      }
      setOptions(new_options);
    }
    setLoading(false);
  }, [char, obj]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
      char.subscription_holder.subscribe("Recalculated", mounted, recalculated);
    }
  }, [char, fetched, setFetched, mounted, recalculated, load]);
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const enabled = shared || (api.user && 
    (char.owner_id === api.user._id || 
      (char.campaign && char.campaign.owner_id === api.user._id)));

  if (!fetched || loading) {
    return (
      <LoadingBox />
    );
  }
  return (
    <SelectStringBox
      name={obj.name}
      options={options}
      value={selected ? selected : ""} 
      disabled={!enabled || processing}
      onChange={ async (changed: string) => {
        setSelected(changed);
        setProcessing(true);
        const new_obj = obj;
        const new_fos = new_obj.feature_options as CharacterSpecialFeature[];
        const removed = new_fos.filter(o => o.source_id !== changed);
        const added = [changed];
        if (removed.length > 0) {
          for (const cfs of removed) {
            if (cfs.source) {
              // console.log(char_subrace);
              for (const char_item of char.items) {
                // console.log(char_item);
                char_item.char_feature_bases = char_item.char_feature_bases.filter(o => 
                  o.ancestries.filter(o2 => o2.true_id === cfs.true_id).length === 0);
                for (const scfs of char_item.sub_char_feature_sources) {
                  scfs.char_feature_bases = scfs.char_feature_bases.filter(o => 
                    o.ancestries.filter(o2 => o2.true_id === cfs.true_id).length === 0);
                }
              }
              char_util.removeCharFeatureBasesFor(char, cfs);
              cfs.char_feature_bases = [];
              cfs.source_id = "";
              cfs.source_name = "";
              cfs.source_type = "";
              cfs.source = null;
            }
          }
        }
        if (added.length > 0) {
          for (const id of added) {
            // Get an empty char_subrace and set the selected_subrace to it
            const cfs = new_fos.find(o => o.source_id === "");
            const selected_sf = data_util.special_features.find(o => o._id === id);
            if (cfs && selected_sf) {
              await api.loadObject(selected_sf);
              await cfs.copy_source({ char, feature_source: selected_sf, parent_cf: obj });
            }
          }
        }
        await char_util.repopulateCharFeatureBases(char);
        char.changed();
        setProcessing(false);
      }} 
    />
  );
}
